import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "home-page" }
const _hoisted_2 = { class: "page-header" }
const _hoisted_3 = {
  key: 0,
  class: "empty-state"
}
const _hoisted_4 = { class: "empty-content" }

import { computed, onMounted, ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { ElMessageBox, ElMessage } from 'element-plus';
import { Plus, Document } from '@element-plus/icons-vue';
import { useRecipeStore } from '@/stores/recipe';
import { useUIStore } from '@/stores/ui';
import { useAuthStore } from '@/stores/auth';
import { canUserModifyRecipe } from '@/utils/recipeUtils';
import SearchAndFilters from '@/components/recipe/SearchAndFilters.vue';
import RecipeTable from '@/components/recipe/RecipeTable.vue';
import RecipeStats from '@/components/recipe/RecipeStats.vue';
import type { Recipe } from '@/types/recipe';


export default /*@__PURE__*/_defineComponent({
  __name: 'Home',
  setup(__props) {

const recipeStore = useRecipeStore();
const uiStore = useUIStore();
const authStore = useAuthStore();
const router = useRouter();
const route = useRoute();

const activeTab = ref('all');
const myRecipes = ref<Recipe[]>([]);

// Computed properties
const pageTitle = computed(() => {
  if (activeTab.value === 'my-recipes') {
    return 'My Recipe Collection';
  }
  return 'Recipe Collection';
});

const pageSubtitle = computed(() => {
  if (activeTab.value === 'my-recipes') {
    return 'Manage and edit your personal recipes';
  }
  return 'Discover, create, and manage your favorite recipes';
});

const displayedRecipes = computed(() => {
  if (activeTab.value === 'my-recipes') {
    return myRecipes.value;
  }
  return uiStore.filteredRecipes;
});

// Handle query parameter for direct linking to "My Recipes"
const initializeTab = () => {
  if (route.query.filter === 'my-recipes' && authStore.isAuthenticated) {
    activeTab.value = 'my-recipes';
  }
};

// Load user's recipes
const loadMyRecipes = async () => {
  if (!authStore.isAuthenticated) {
    return;
  }

  try {
    uiStore.setLoading(true);
    const userRecipes = await recipeStore.fetchMyRecipes();
    myRecipes.value = userRecipes || [];
  } catch (error) {
    console.error('Error loading user recipes:', error);
    ElMessage.error('Failed to load your recipes');
  } finally {
    uiStore.setLoading(false);
  }
};

// Handle tab changes
const handleTabChange = (tabName: string) => {
  activeTab.value = tabName;

  // Update URL without navigation
  const query = tabName === 'my-recipes' ? { filter: 'my-recipes' } : {};
  router.replace({ query });

  // Load appropriate data
  if (tabName === 'my-recipes') {
    loadMyRecipes();
    // Update UI store to filter my recipes
    uiStore.recipes = myRecipes.value;
  } else {
    // Load all recipes
    recipeStore.fetchRecipes();
  }

  // Reset pagination
  uiStore.currentPage = 1;
};

const handleRowClick = (row: Recipe) => {
  router.push(`/recipe/${row.id}`);
};

const handleCreateRecipe = () => {
  if (authStore.isAuthenticated) {
    router.push('/create');
  } else {
    router.push('/login');
  }
};

const editRecipe = (recipe: Recipe) => {
  // Check ownership before allowing edit
  if (
    authStore.isAuthenticated &&
    canUserModifyRecipe(recipe, authStore.user)
  ) {
    router.push(`/edit/${recipe.id}`);
  } else {
    ElMessage.warning('You can only edit your own recipes');
  }
};

const confirmDelete = async (recipe: Recipe) => {
  // Check ownership before allowing delete
  if (
    !authStore.isAuthenticated ||
    !canUserModifyRecipe(recipe, authStore.user)
  ) {
    ElMessage.warning('You can only delete your own recipes');
    return;
  }

  try {
    await ElMessageBox.confirm(
      `Are you sure you want to delete "${recipe.title}"?`,
      'Confirm Delete',
      {
        confirmButtonText: 'Yes, Delete',
        cancelButtonText: 'Cancel',
        type: 'warning',
      }
    );

    const success = await recipeStore.deleteRecipe(recipe.id!);
    if (success) {
      ElMessage.success('Recipe deleted successfully!');

      // Refresh the appropriate list
      if (activeTab.value === 'my-recipes') {
        loadMyRecipes();
      } else {
        recipeStore.fetchRecipes();
      }
    } else {
      ElMessage.error('Failed to delete recipe');
    }
  } catch {
    // User cancelled
  }
};

// Watch for authentication changes
watch(
  () => authStore.isAuthenticated,
  (isAuth) => {
    if (!isAuth && activeTab.value === 'my-recipes') {
      activeTab.value = 'all';
      router.replace({ query: {} });
    }
  }
);

onMounted(() => {
  initializeTab();

  // Load initial data based on tab
  if (activeTab.value === 'my-recipes') {
    loadMyRecipes();
  } else {
    recipeStore.fetchRecipes();
  }
});

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h2", null, _toDisplayString(pageTitle.value), 1),
      _createElementVNode("p", null, _toDisplayString(pageSubtitle.value), 1)
    ]),
    _createVNode(SearchAndFilters, {
      search: _unref(uiStore).searchQuery,
      "onUpdate:search": _cache[0] || (_cache[0] = ($event: any) => ((_unref(uiStore).searchQuery) = $event)),
      difficulty: _unref(uiStore).difficultyFilter,
      "onUpdate:difficulty": _cache[1] || (_cache[1] = ($event: any) => ((_unref(uiStore).difficultyFilter) = $event)),
      "ingredient-count": _unref(uiStore).ingredientCountFilter,
      "onUpdate:ingredientCount": _cache[2] || (_cache[2] = ($event: any) => ((_unref(uiStore).ingredientCountFilter) = $event)),
      onCreateRecipe: handleCreateRecipe
    }, null, 8, ["search", "difficulty", "ingredient-count"]),
    _createVNode(RecipeStats, { recipes: displayedRecipes.value }, null, 8, ["recipes"]),
    _createVNode(RecipeTable, {
      recipes: _unref(uiStore).paginatedRecipes,
      loading: _unref(uiStore).isLoading,
      "current-page": _unref(uiStore).currentPage,
      "page-size": _unref(uiStore).pageSize,
      total: _unref(uiStore).filteredRecipes.length,
      "onUpdate:currentPage": _cache[3] || (_cache[3] = ($event: any) => (_unref(uiStore).currentPage = $event)),
      "onUpdate:pageSize": _cache[4] || (_cache[4] = ($event: any) => (_unref(uiStore).pageSize = $event)),
      onRowClick: handleRowClick,
      onEditRecipe: editRecipe,
      onDeleteRecipe: confirmDelete
    }, null, 8, ["recipes", "loading", "current-page", "page-size", "total"]),
    (
        activeTab.value === 'my-recipes' &&
        myRecipes.value.length === 0 &&
        !_unref(uiStore).isLoading
      )
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_unref(Document), { class: "empty-icon" }),
            _cache[6] || (_cache[6] = _createElementVNode("h3", null, "No recipes yet", -1)),
            _cache[7] || (_cache[7] = _createElementVNode("p", null, " You haven't created any recipes yet. Start by creating your first recipe! ", -1)),
            _createVNode(_component_el_button, {
              type: "primary",
              class: "custom-button",
              size: "large",
              onClick: handleCreateRecipe
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(Plus), { class: "button-icon" }),
                _cache[5] || (_cache[5] = _createTextVNode(" Create Your First Recipe "))
              ]),
              _: 1,
              __: [5]
            })
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})